import { ApiErrorBody, CSRF_HEADER, CSRF_VALUE, type ErrorCode, TENANT_HEADER } from '@serievo/contracts';
import type { z } from 'zod';

/**
 * Phiên đăng nhập là cookie httpOnly do API đặt; trình duyệt tự gửi kèm (cùng origin).
 * Token dev (npm run token:dev) chỉ dùng khi chạy `vite` ở máy dev, không có trong bản build.
 */
const DEV_TOKEN_KEY = 'serievo.devToken';

export const devToken = {
  get(): string | null {
    if (!import.meta.env.DEV) return null;
    try {
      return sessionStorage.getItem(DEV_TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set(token: string | null): void {
    if (!import.meta.env.DEV) return;
    try {
      if (token) sessionStorage.setItem(DEV_TOKEN_KEY, token);
      else sessionStorage.removeItem(DEV_TOKEN_KEY);
    } catch {
      /* trình duyệt chặn storage: bỏ qua */
    }
  },
};

export class ApiError extends Error {
  override name = 'ApiError';
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'NETWORK',
    message: string,
    readonly correlationId?: string,
  ) {
    super(message);
  }
}

export interface RequestOpts {
  tenantId?: string;
  signal?: AbortSignal;
}

async function send(method: string, path: string, body: unknown, opts: RequestOpts): Promise<Response> {
  const token = devToken.get();
  try {
    return await fetch(`/api/v1${path}`, {
      method,
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(method !== 'GET' ? { [CSRF_HEADER]: CSRF_VALUE } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(opts.tenantId ? { [TENANT_HEADER]: opts.tenantId } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, 'NETWORK', 'Không kết nối được máy chủ');
  }
}

async function errorOf(res: Response): Promise<ApiError> {
  const json: unknown = await res.json().catch(() => null);
  const e = ApiErrorBody.safeParse(json);
  if (e.success) return new ApiError(res.status, e.data.error.code, e.data.error.message, e.data.correlation_id);
  return new ApiError(res.status, 'INTERNAL', `Máy chủ trả lỗi ${res.status}`);
}

export async function apiGet<S extends z.ZodType>(path: string, schema: S, opts: RequestOpts = {}): Promise<z.infer<S>> {
  const res = await send('GET', path, undefined, opts);
  if (!res.ok) throw await errorOf(res);
  return schema.parse(await res.json());
}

export async function apiPost<S extends z.ZodType>(
  path: string,
  body: unknown,
  schema: S,
  opts: RequestOpts = {},
): Promise<z.infer<S>> {
  const res = await send('POST', path, body ?? {}, opts);
  if (!res.ok) throw await errorOf(res);
  return schema.parse(await res.json());
}

/** POST không cần đọc kết quả (204 hoặc bỏ qua body). */
export async function apiPostVoid(path: string, body: unknown, opts: RequestOpts = {}): Promise<void> {
  const res = await send('POST', path, body ?? {}, opts);
  if (!res.ok) throw await errorOf(res);
}

/** Tải file (Excel) qua fetch để giữ cookie + header tenant, rồi cho trình duyệt lưu. */
export async function apiDownload(path: string, fallbackName: string, opts: RequestOpts = {}): Promise<void> {
  const res = await send('GET', path, undefined, opts);
  if (!res.ok) throw await errorOf(res);
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const name = /filename\*=UTF-8''([^;]+)/.exec(cd)?.[1];
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name ? decodeURIComponent(name) : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

const VI = new Intl.NumberFormat('vi-VN');

/** Tiền VND dạng chuỗi từ API → "1.234.567". Không đổi qua number để không mất chính xác. */
export function formatMoney(v: string): string {
  return VI.format(BigInt(v));
}

export const formatInt = (n: number) => VI.format(n);
